<template>
  <p class="activity-header">
    Оценка и развитие
  </p>

  <div class="segment-block">
    <a-segmented v-model:value="selectedFilter" :options="passingTypeData" />
  </div>

  <a-table
      rowKey="id"
      :columns="columns"
      :data-source="detailedActivity"
      :scroll="{ x: 600 }"
      :pagination="false"
      :loading="tableLoading"
      :table-layout="'fixed'"
  >
    <template #bodyCell="{ column, text, record }">
      <template v-if="column.dataIndex === 'status'">
        <a-tag color="green" v-if="record.original_status === 'finished'">
          {{ text }}
        </a-tag>
        <a-tag class="yellow-tag" v-else-if="record.original_status === 'new'">
          {{ text }}
        </a-tag>
        <a-tag class="blue-tag" v-else>
          {{ text }}
        </a-tag>
      </template>

      <template v-if="column.dataIndex === 'name' && record.type === 'test' && record.original_status === 'finished'">
        <div>
            {{ text }}
        </div>
        <a-dropdown v-if="getRight('superadmin') || getRight('admin') || record.allow_view_report">
          <a class="ant-dropdown-link" @click.prevent>
            Скачать отчет
            <DownOutlined />
          </a>
          <template #overlay>
            <a-menu>
              <a-menu-item :key="record.code">
                  <span @click="downloadReport(record.code, (record.report_type === 'management' ? 'employee' : record.report_type))">
                    {{ record.name + ' ' + (record.report_type === 'management' ? 'Отчет для сотрудника' : '') }}
                  </span>
              </a-menu-item>
              <a-menu-item v-if="record.report_type === 'management'" :key="record.code + 'employer'">
                  <span @click="downloadReport(record.code, 'employer')">
                    {{ record.name + ' ' + (record.report_type === 'management' ? 'Отчет для заказчика' : '') }}
                  </span>
              </a-menu-item>

              <template v-for="report in record.related_passings">
                <template v-if="report.report_type === 'management'">

                  <a-menu-item :key="report.code">
                    <span @click="downloadReport(report.code, 'employee')">
                      {{ getReportName(report.report_type) + ' Отчет для сотрудника' }}
                    </span>
                  </a-menu-item>

                  <a-menu-item :key="report.code + 'employer'">
                  <span @click="downloadReport(report.code, 'employer')">
                    {{ getReportName(report.report_type) + ' Отчет для заказчика' }}
                  </span>
                  </a-menu-item>
                </template>

                <a-menu-item v-else :key="report.code">
                  <span @click="downloadReport(report.code, report.report_type)">
                    {{ getReportName(report.report_type) }}
                  </span>
                </a-menu-item>
              </template>

              <a-menu-item :key="'new_report'" v-if="getRight('admin')">
                <div>
                  <PlusOutlined />
                  <span @click="() => setActiveModal(record)" class="new-report-types-button">
                    Получить другие отчеты
                  </span>
                </div>
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
      </template>

    </template>

    <template #emptyText>
      <div class="no-data-wrapper">
        <img :src="NotFoundIcon" class="no-data-image" alt="Нет результатов"/>
        <div class="no-data-text">
          Нет данных для отображения. Для назначения курсов обратитесь к руководителю.
        </div>
      </div>
    </template>
  </a-table>

  <div class="pagination-block">
    <a-pagination v-model:current="currentPage"
                  :page-size="entitiesPerPage"
                  :buildOptionText="(count) => `${count.value} / стр`"
                  :show-total="isMobile ? undefined : (total => `Всего ${total ?? 0} элементов`)"
                  :total="userActivitiesTotal ?? 0"
                  :show-less-items="isMobile"
                  @change="handlePageChange"
    />
  </div>

  <GenerateAdditionalReport
      v-if="showModal"
      :originalPassing="passingToGenerateAdditionalReports"
      :open="showModal"
      @closeForm="() => {
        passingToGenerateAdditionalReports = null;
        showModal = false;
      }"
      @generated="refresh"
  />

</template>

<script>
import {DownOutlined, PlusOutlined} from "@ant-design/icons-vue";
import {computed} from "@vue/reactivity";
import NotFoundIcon from "@/assets/images/icons/no_data.svg";
import {onBeforeMount, reactive, ref, watch} from "@vue/runtime-core";
import {useStore} from "vuex";
import {getTestStatus} from "@/feature/group-control/utils/groupPassingUtils";
import {getCourseStatus} from "@/feature/group-control/utils/coursePassingUtils";
import useCheckRights from "@/mixins/useCheckRights";
import GenerateAdditionalReport from "@/feature/testing/forms/GenerateAdditionalReport.vue";
import getReportName from "@/mixins/getReportName";
import store from "@/store";
import {message} from "ant-design-vue";
import useBreakpoints from "@/mixins/useBreakpoints";

export default {
  components: {GenerateAdditionalReport, DownOutlined, PlusOutlined},
  props: {
    userId: [String, Number],
  },
  setup(props) {

    const columns = computed(() => [
      {
        title: 'Событие',
        dataIndex: 'name',
        sorter: false,
        width: 250,
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        sorter: false,
        width: 90,
      },
      {
        title: 'Дата назначения',
        dataIndex: 'started_at',
        sorter: false,
        width: 120,
      },
      {
        title: 'Дата завершения',
        dataIndex: 'finished_at',
        sorter: false,
        width: 120,
      },
    ]);

    const entitiesPerPage = ref(10);
    const currentPage = ref(1);
    const tableLoading = ref(true);
    const store = useStore();
    const selectedFilter = ref('Все');
    const { isMobile } = useBreakpoints();

    const userActivity = computed(() => store.state.users.recentActivities);
    const userActivitiesTotal = computed(() => store.state.users.totalActivities);
    const detailedActivity = ref();

    const { getRight } = useCheckRights();

    // пагинация
    const retrieveEntitiesPerPage = () => {
      let storageValue = localStorage.getItem('perPage');
      entitiesPerPage.value = storageValue ? Number(storageValue) : 10;
    }

    const setEntitiesPerPage = (num) => {
      localStorage.setItem('perPage', num)
    }

    const handlePageChange = (current, perPage) => {
      if (entitiesPerPage.value !== perPage) {
        entitiesPerPage.value = perPage;
        setEntitiesPerPage(entitiesPerPage.value);
      }

      currentPage.value = current;
      fetchActivities();
    }

    const fetchActivities = () => {
      tableLoading.value = true;

      store.dispatch('users/getUserActivities', {
        userId: props.userId,
        perPage: entitiesPerPage.value,
        page: currentPage.value,
        type: handleFilter(),
      }).then(() => {
        detailedActivity.value = userActivity.value.map((el) => {
          return {
            name: el.code ? getReportName(el.report_type) : el.name,
            code: el.code ?? null,
            type: el.code ? 'test' : 'course',
            report_type: el.report_type ?? null,
            related_passings: el.related_passings ?? [],
            allow_view_report: el.allow_view_report ?? null,
            status: el.code ? getTestStatus(el) : getCourseStatus(el),
            original_status: el.status,
            started_at: el.activated_at ? el.activated_at : null,
            finished_at: el.finished_at ?? null,
          }
        });
      }).finally(() => {
        tableLoading.value = false;
      })
    }

    const filterTranslate = {
      all: 'Все',
      courses: 'Развитие',
      tests: 'Оценка'
    }

    const passingTypeData = reactive(['Все', 'Оценка', 'Развитие']);

    const handleFilter = () => {
      return Object.keys(filterTranslate).find(k => filterTranslate[k] === selectedFilter.value);
    }

    const reportIsGenerating = ref(false);

    const downloadReport = (code, type) => {
      reportIsGenerating.value = true;
      const generationMessageKey = `report-generation-${code}`;
      message.info({
        content: 'Идет генерация отчета, пожалуйста, подождите',
        duration: 0,
        key: generationMessageKey,
      });

      store.dispatch("group/getReport", {
        passing: [code],
        type: type,
      }).finally(() => {
        message.destroy(generationMessageKey);
        reportIsGenerating.value = false;
      })
    };

    const passingToGenerateAdditionalReports = ref(null);
    const showModal = ref(false);

    const setActiveModal = (passing) => {
      passingToGenerateAdditionalReports.value = passing;
      showModal.value = true;
    }

    const refresh = () => {
      showModal.value = false;
      passingToGenerateAdditionalReports.value = null;
      fetchActivities();
    }

    onBeforeMount(() => {
      retrieveEntitiesPerPage();
      fetchActivities();
    });

    watch(() => selectedFilter.value, () => {
      fetchActivities();
    });

    return {
      NotFoundIcon,
      entitiesPerPage,
      handlePageChange,
      columns,
      tableLoading,
      currentPage,
      passingTypeData,
      selectedFilter,
      userActivitiesTotal,
      userActivity,
      detailedActivity,
      getRight,
      downloadReport,
      passingToGenerateAdditionalReports,
      refresh,
      setActiveModal,
      showModal,
      getReportName,
      isMobile
    }
  }
}
</script>

<style scoped lang="scss">
  .activity-header {
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 20px;
  }

  .segment-block {
    margin-bottom: 20px;
  }

  .pagination-block {
    margin-left: auto;
    margin-top: 20px;
  }

  :deep(.ant-table-thead >tr>th) {
    padding: 5px !important;
  }

  :deep(.ant-table-tbody>tr:not(.ant-table-measure-row)>td) {
    padding: 10px 5px !important;
  }

  .ant-dropdown-link {
    display: flex;
    column-gap: 5px;
    align-items: center;
    color: var(--main-green);

    :deep(.anticon-down) {
      font-size: 12px !important;
    }
  }
</style>
